/**
 * Sections for the User guide / SOP page (step-by-step guidance).
 * Keys pair with useKivunovaTranslation fallbacks.
 */
import { SopStepRichVariant } from "../enums";
import type { SopSection } from "../types";

export const USER_GUIDE_SECTIONS: SopSection[] = [
  {
    id: "before-you-begin",
    titleKey: "sop-before-begin-title",
    titleFallback: "Before you begin",
    steps: [
      {
        bodyKey: "sop-before-begin-1",
        bodyFallback:
          "Use an up-to-date browser and a stable internet connection."
      },
      {
        bodyKey: "sop-before-begin-2",
        bodyFallback:
          "Keep your company email handy, you will use it to sign in and recover access."
      }
    ]
  },
  {
    id: "registration",
    titleKey: "sop-registration-title",
    titleFallback: "Create your account & company",
    steps: [
      {
        bodyKey: "sop-registration-1",
        bodyFallback:
          "From the welcome screen, choose Get started or Register and enter your company details (name, address, currency, etc.)."
      },
      {
        bodyKey: "sop-registration-2",
        bodyFallback:
          "Complete your user profile (name, email, password) and verify your email when prompted."
      },
      {
        bodyKey: "sop-registration-3",
        bodyFallback:
          "After verification, sign in. You may see your home screen, ready to set up stores and products."
      }
    ]
  },
  {
    id: "login",
    titleKey: "sop-login-title",
    titleFallback: "Sign in & password recovery",
    steps: [
      {
        bodyKey: "sop-login-1",
        bodyFallback:
          "Open Login from the welcome screen. Enter the email and password associated with your account."
      },
      {
        bodyKey: "sop-login-2",
        bodyFallback:
          "If you forgot your password, use Forgot password and follow the reset link sent to your email."
      },
      {
        bodyKey: "sop-login-3",
        bodyFallback:
          "Ensure caps lock is off and you are using the correct company email if your organization uses strict login policies."
      }
    ]
  },
  {
    id: "portal-dashboard",
    titleKey: "sop-dashboard-title",
    titleFallback: "Home screen",
    steps: [
      {
        bodyKey: "sop-dashboard-1",
        bodyFallback:
          "After you sign in, the menu on the left shows the areas you can open based on your role."
      },
      {
        bodyKey: "sop-dashboard-2",
        bodyFallback:
          "The overview shows sales, stock movement, and top performers. You can filter by date when available."
      },
      {
        bodyKey: "sop-dashboard-3",
        bodyFallback:
          "Use Profile and Company from the sidebar to manage your user settings and organization profile."
      }
    ]
  },
  {
    id: "stores",
    titleKey: "sop-stores-title",
    titleFallback: "Stores & locations",
    steps: [
      {
        bodyKey: "sop-stores-1",
        bodyFallback:
          "Company admins can create stores under Stores—each location can have its own staff and inventory context."
      },
      {
        bodyKey: "sop-stores-2",
        bodyFallback:
          "Assign store admins or staff so day-to-day operations happen at the right location."
      },
      {
        bodyKey: "sop-stores-3",
        bodyFallback:
          "Open a store to review details, contacts, and linked activity depending on your permissions."
      }
    ]
  },
  {
    id: "products-vs-items",
    titleKey: "sop-products-items-title",
    titleFallback: "Products vs product items",
    steps: [
      {
        bodyKey: "sop-products-items-1",
        bodyFallback:
          "A product is what you sell: name, code, category, price, unit, and how it is sold."
      },
      {
        bodyKey: "sop-products-items-2",
        bodyFallback:
          "Product items are the actual units in stock, often by batch, serial number, or shelf location (available, sold, or in transit)."
      },
      {
        bodyKey: "sop-products-items-3",
        bodyFallback:
          "Typical flow: add categories, create products, then record stock so quantities match what you have in each store."
      }
    ]
  },
  {
    id: "categories",
    titleKey: "sop-categories-title",
    titleFallback: "Categories",
    steps: [
      {
        bodyKey: "sop-categories-1",
        bodyFallback:
          "Group products into categories before or while adding products. Clear categories make reports and checkout easier."
      },
      {
        bodyKey: "sop-categories-2",
        bodyFallback:
          "Update categories as your range grows. Your administrator can control who is allowed to change products."
      }
    ]
  },
  {
    id: "orders",
    titleKey: "sop-orders-title",
    titleFallback: "Orders, sales & purchases",
    steps: [
      {
        bodyKey: "sop-orders-1",
        bodyFallback:
          "Use Orders to record sales and purchases in the way that fits your business."
      },
      {
        bodyKey: "sop-orders-2",
        bodyFallback:
          "Depending on your settings, you may get invoices, receipts, or purchase records. Follow each step and check the totals."
      },
      {
        bodyKey: "sop-orders-3",
        bodyFallback:
          "When you complete an order, stock updates automatically so your records stay accurate."
      }
    ]
  },
  {
    id: "employees",
    titleKey: "sop-employees-title",
    titleFallback: "People & roles",
    steps: [
      {
        bodyKey: "sop-employees-1",
        bodyFallback:
          "Company administrators invite or manage staff under People. Each person gets the access they need."
      },
      {
        bodyKey: "sop-employees-2",
        bodyFallback:
          "Separate company-wide admins from store-level roles so sensitive actions stay controlled."
      }
    ]
  },
  {
    id: "membership-apps",
    titleKey: "sop-membership-apps-title",
    titleFallback: "Membership & installing apps",
    steps: [
      {
        bodyKey: "sop-membership-apps-1",
        bodyFallback:
          "Open Membership, then Plans & Subscriptions, to see your plan and billing."
      },
      {
        bodyKey: "sop-membership-apps-2",
        bodyFallback:
          "Install the computer or phone app from Download apps when available. The same sign-in works everywhere."
      },
      {
        bodyKey: "sop-membership-apps-3",
        bodyFallback:
          "Need human help? Use the floating Contact us button, or email support.",
        richVariant: SopStepRichVariant.CONTACT_HELP
      }
    ]
  }
];
